import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Loader2, CheckCircle2 } from "lucide-react";

const Section = ({ n, title, desc, children }) => (
  <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 sm:p-6">
    <div className="flex items-center gap-3 mb-4">
      <span className="h-7 w-7 rounded-full bg-[#F05A28] text-white text-xs font-bold flex items-center justify-center">{n}</span>
      <div>
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {desc && <p className="text-xs text-slate-500">{desc}</p>}
      </div>
    </div>
    {children}
  </div>
);

export default function NewReport() {
  const { data: cfg } = useConfig();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    report_date: new Date().toISOString().slice(0, 10),
    province: user?.province || "", territory: "", health_zone: user?.health_zone || "",
    health_area: "", facility: "", organization: "FHI 360",
  });
  const [activities, setActivities] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [findings, setFindings] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [recs, setRecs] = useState([]);
  const [story, setStory] = useState("");

  useEffect(() => {
    if (!editId) return;
    api.get(`/reports/${editId}`).then(({ data }) => {
      setForm({
        report_date: data.report_date, province: data.province, territory: data.territory,
        health_zone: data.health_zone, health_area: data.health_area || "",
        facility: data.facility || "", organization: data.organization || "FHI 360",
      });
      setActivities(data.activities || []);
      setMetrics(data.metrics || {});
      setFindings(data.findings || []);
      setChallenges(data.challenges || []);
      setRecs(data.recommendations || []);
      setStory(data.success_story || "");
    }).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  }, [editId]);

  const geo = cfg?.geo || {};
  const provinces = Object.keys(geo);
  const territories = form.province ? Object.keys(geo[form.province]?.territories || {}) : [];
  const zones = form.province && form.territory ? geo[form.province]?.territories?.[form.territory] || [] : [];

  const metricLabels = useMemo(() => Object.fromEntries((cfg?.metrics || []).map((m) => [m.code, m.name])), [cfg]);
  const visibleMetrics = useMemo(() => {
    const am = cfg?.activity_metrics || {};
    const set = new Set();
    activities.forEach((a) => (am[a] || []).forEach((m) => set.add(m)));
    return [...set];
  }, [activities, cfg]);

  const toggleActivity = (code) =>
    setActivities((a) => (a.includes(code) ? a.filter((x) => x !== code) : [...a, code]));

  const setM = (code, v) => setMetrics((m) => ({ ...m, [code]: v }));

  const submit = async () => {
    if (!form.province || !form.health_zone) { toast.error(t("Veuillez sélectionner la Province et la Zone de Santé.")); return; }
    if (activities.length === 0) { toast.error(t("Sélectionnez au moins une activité réalisée.")); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        activities,
        metrics: Object.fromEntries(Object.entries(metrics).map(([k, v]) => [k, Number(v) || 0])),
        findings, challenges, recommendations: recs, success_story: story,
      };
      if (editId) {
        await api.put(`/reports/${editId}`, payload);
        toast.success(t("Rapport mis à jour."));
      } else {
        await api.post("/reports", payload);
        toast.success(t("Rapport quotidien soumis avec succès."));
      }
      navigate("/reports");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="new-report-page">
      <PageHeader title={editId ? t("Modifier le Rapport") : t("Rapport Quotidien de Terrain")} subtitle={t("Saisie rapide (< 5 min) · répondez uniquement aux activités réalisées aujourd'hui")} />
      <div className="space-y-4">
        <Section n="A" title={t("Identification")} desc={t("Pré-rempli depuis votre profil")}>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" value={form.report_date} onChange={(e) => setForm({ ...form, report_date: e.target.value })} data-testid="report-date" /></div>
            <div><Label>Organisation</Label><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="report-org" /></div>
            <div>
              <Label>Province *</Label>
              <Select value={form.province} onValueChange={(v) => setForm({ ...form, province: v, territory: "", health_zone: "" })}>
                <SelectTrigger data-testid="report-province"><SelectValue placeholder={t("Choisir")} /></SelectTrigger>
                <SelectContent>{provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("Territoire")}</Label>
              <Select value={form.territory} onValueChange={(v) => setForm({ ...form, territory: v, health_zone: "" })} disabled={!form.province}>
                <SelectTrigger data-testid="report-territory"><SelectValue placeholder={t("Choisir")} /></SelectTrigger>
                <SelectContent>{territories.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("Zone de Santé")} *</Label>
              <Select value={form.health_zone} onValueChange={(v) => setForm({ ...form, health_zone: v })} disabled={!form.territory}>
                <SelectTrigger data-testid="report-zone"><SelectValue placeholder={t("Choisir")} /></SelectTrigger>
                <SelectContent>{zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("Aire de Santé")}</Label>
              <Select value={form.health_area} onValueChange={(v) => setForm({ ...form, health_area: v })}>
                <SelectTrigger data-testid="report-area"><SelectValue placeholder={t("Choisir")} /></SelectTrigger>
                <SelectContent>{(cfg?.health_areas || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </Section>

        <Section n="B" title={t("Activités réalisées aujourd'hui")} desc={t("Cochez · les champs chiffrés s'afficheront selon vos choix")}>
          <div className="grid sm:grid-cols-2 gap-2">
            {(cfg?.activities || []).map((a) => (
              <label key={a.code} className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${activities.includes(a.code) ? "border-[#F05A28] bg-[#FFF3EE]" : "border-slate-200 hover:bg-slate-50"}`}>
                <Checkbox checked={activities.includes(a.code)} onCheckedChange={() => toggleActivity(a.code)} data-testid={`act-${a.code}`} />
                <span className="text-sm text-slate-700">{a.name}</span>
              </label>
            ))}
          </div>
        </Section>

        {visibleMetrics.length > 0 && (
          <Section n="C" title={t("Chiffres de surveillance")} desc={t("Zéro accepté · ne renseignez que ce qui est pertinent")}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleMetrics.map((code) => (
                <div key={code}>
                  <Label className="text-xs">{metricLabels[code]}</Label>
                  <Input type="number" min="0" value={metrics[code] ?? ""} placeholder="0" onChange={(e) => setM(code, e.target.value)} data-testid={`metric-${code}`} />
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section n="E" title={t("Constats clés")} desc={t("Observation épidémiologique importante du jour")}>
          {findings.map((f, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mb-3 items-end border-b border-slate-100 pb-3">
              <div>
                <Label className="text-xs">Catégorie</Label>
                <Select value={f.category} onValueChange={(v) => setFindings(findings.map((x, j) => j === i ? { ...x, category: v } : x))}>
                  <SelectTrigger data-testid={`finding-cat-${i}`}><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{(cfg?.finding_categories || []).map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Explication</Label><Input value={f.description} onChange={(e) => setFindings(findings.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} /></div>
              <Button variant="ghost" size="icon" onClick={() => setFindings(findings.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setFindings([...findings, { category: "", description: "", severity: "Moyenne" }])} data-testid="add-finding"><Plus className="h-4 w-4 mr-1" /> {t("Ajouter un constat")}</Button>
        </Section>

        <Section n="F" title={t("Défis rencontrés")}>
          {challenges.map((c, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mb-3 items-end border-b border-slate-100 pb-3">
              <div>
                <Label className="text-xs">Catégorie</Label>
                <Select value={c.category} onValueChange={(v) => setChallenges(challenges.map((x, j) => j === i ? { ...x, category: v } : x))}>
                  <SelectTrigger data-testid={`challenge-cat-${i}`}><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{(cfg?.challenge_categories || []).map((c2) => <SelectItem key={c2.code} value={c2.code}>{c2.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Gravité</Label>
                <Select value={c.severity} onValueChange={(v) => setChallenges(challenges.map((x, j) => j === i ? { ...x, severity: v } : x))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(cfg?.priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setChallenges(challenges.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setChallenges([...challenges, { category: "", description: "", severity: "Moyenne" }])} data-testid="add-challenge"><Plus className="h-4 w-4 mr-1" /> {t("Ajouter un défi")}</Button>
        </Section>

        <Section n="G" title={t("Recommandations / Actions requises")} desc={t("Alimente automatiquement le suivi des recommandations")}>
          {recs.map((r, i) => (
            <div key={i} className="space-y-3 mb-4 border-b border-slate-100 pb-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Label className="text-xs">Recommandation</Label><Input value={r.recommendation} onChange={(e) => setRecs(recs.map((x, j) => j === i ? { ...x, recommendation: e.target.value } : x))} data-testid={`rec-text-${i}`} /></div>
                <div><Label className="text-xs">Action proposée</Label><Input value={r.proposed_action} onChange={(e) => setRecs(recs.map((x, j) => j === i ? { ...x, proposed_action: e.target.value } : x))} /></div>
                <div><Label className="text-xs">Responsable</Label><Input value={r.owner} onChange={(e) => setRecs(recs.map((x, j) => j === i ? { ...x, owner: e.target.value } : x))} /></div>
                <div>
                  <Label className="text-xs">Priorité</Label>
                  <Select value={r.priority} onValueChange={(v) => setRecs(recs.map((x, j) => j === i ? { ...x, priority: v } : x))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(cfg?.priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Échéance</Label><Input type="date" value={r.due_date || ""} onChange={(e) => setRecs(recs.map((x, j) => j === i ? { ...x, due_date: e.target.value } : x))} /></div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setRecs(recs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 mr-1 text-red-500" /> Retirer</Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setRecs([...recs, { recommendation: "", proposed_action: "", owner: "", owner_org: "", priority: "Moyenne", due_date: "" }])} data-testid="add-rec"><Plus className="h-4 w-4 mr-1" /> {t("Ajouter une recommandation")}</Button>
        </Section>

        <Section n="H" title={t("Succès / Impact (optionnel)")}>
          <Textarea value={story} onChange={(e) => setStory(e.target.value)} placeholder={t("Quel résultat positif a été obtenu aujourd'hui ?")} data-testid="report-story" />
        </Section>

        <div className="flex justify-end gap-3 pb-10">
          <Button variant="outline" onClick={() => navigate("/reports")}>{t("Annuler")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-report" className="bg-[#F05A28] hover:bg-[#D84A1B]">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} {t("Soumettre le rapport")}
          </Button>
        </div>
      </div>
    </div>
  );
}
